import type { MaximizablePanelId } from "./layout.ts";
import { RUNG_LADDER, rungKey, type RungKey } from "./rungLadder.ts";

export const FOCUS_AREAS = ["chart", "tide", "rail"] as const;
export type FocusArea = (typeof FOCUS_AREAS)[number];
export type FocusChartId = "primaryA" | "primaryB";

export const FOCUS_TIDE_ROW = [
  { id: "index-0dte", tide: "index", expiration: "zero_dte" },
  { id: "index-weekly", tide: "index", expiration: "weekly" },
  { id: "etf-0dte", tide: "etf", expiration: "zero_dte" },
  { id: "etf-weekly", tide: "etf", expiration: "weekly" },
] as const;
export type FocusTideScope = (typeof FOCUS_TIDE_ROW)[number];

export const FOCUS_GAP_PX = 14;
export const FOCUS_VIEWPORT_INSET_PX = 28;
export const FOCUS_STACK_BREAKPOINT_PX = 720;
export const FOCUS_FOLD_BREAKPOINT_PX = 1440;
export const FOCUS_PROPORTIONS = Object.freeze({
  // ExpiryTidePanel.svelte:430-433 pins 136px of chart; measured panel chrome makes 240px the cell floor.
  tide: { minPx: 240 },
  chart: { minPx: 520, priceFraction: 0.74, cvdFraction: 0.26 },
  rail: {
    fraction: 0.21,
    minPx: 360,
    maxPx: 520,
    heightFraction: 0.5,
    minHeightPx: 240,
  },
});
export const FOCUS_DEFAULT_BARS = 180;
export const FOCUS_RIGHT_MARGIN_BARS = 6;

export function isCandleFocus(panel: MaximizablePanelId | null): panel is FocusChartId {
  return panel === "primaryA" || panel === "primaryB";
}

export interface FocusCellFit {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  clientWidth: number;
  clientHeight: number;
  scrollWidth: number;
  scrollHeight: number;
}

export interface FocusFit {
  viewport: { width: number; height: number };
  usable: { x: number; y: number; w: number; h: number };
  stacked?: true;
  folded: boolean;
  chart: FocusCellFit;
  tideRow: FocusCellFit;
  tides: [FocusCellFit, FocusCellFit, FocusCellFit, FocusCellFit];
  rail0dte: FocusCellFit;
  railAllExpiry: FocusCellFit;
  renderCells: FocusCellFit[];
}

export interface FocusContentHeights {
  /** Natural height of the linked chart card, including its own title and controls. */
  chart: number;
  /** Measured native pane-2 height. It is diagnostic input, never a separate strip allocation. */
  vsplit: number;
  tides: readonly [number, number, number, number];
  rail0dte: number;
  railAllExpiry: number;
}

export interface FocusLogicalRange {
  from: number;
  to: number;
}

export interface FocusRenderFixture {
  viewport: { width: number; height: number };
  cells: Array<{ id: string; x: number; y: number; w: number; h: number }>;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Each focus rail card owns at most one half of the usable rail stack, less its shared gap. */
export function focusRailCellHeightBudget(usableHeight: number): number {
  return Math.max(
    FOCUS_PROPORTIONS.rail.minHeightPx,
    Math.floor((usableHeight - FOCUS_GAP_PX) * FOCUS_PROPORTIONS.rail.heightFraction),
  );
}

/** Natural rail-card content, independent of the fitted section height that will contain it. */
export function railCardContentHeight(
  chromeHeights: readonly number[],
  ladderScrollHeights: readonly number[],
  gapHeights: readonly number[] = [],
): number {
  const sum = (values: readonly number[], label: string): number => {
    let total = 0;
    for (const value of values) {
      if (!Number.isFinite(value) || value < 0) {
        throw new RangeError(
          `FOCUS FIT REFUSED — rail card ${label} height ${String(value)} is not non-negative and finite`,
        );
      }
      total += value;
    }
    return total;
  };
  return Math.ceil(
    sum(chromeHeights, "chrome") +
    sum(ladderScrollHeights, "ladder") +
    sum(gapHeights, "gap"),
  );
}

export type RailMeasurementNode =
  | { kind: "leaf"; height: number; marginTop: number; marginBottom: number }
  | { kind: "ladder"; scrollHeight: number; marginTop: number; marginBottom: number }
  | {
      kind: "container";
      frameHeight: number;
      rowGap: number;
      children: readonly RailMeasurementNode[];
    };

/** Sum the same nested flex tree that the rail renders, including notes and every inter-child gap. */
export function measuredRailCardContentHeight(root: RailMeasurementNode): number {
  const contentHeights: number[] = [];
  const gapHeights: number[] = [];
  const collect = (node: RailMeasurementNode): void => {
    if (node.kind === "leaf") {
      contentHeights.push(node.height + node.marginTop + node.marginBottom);
      return;
    }
    if (node.kind === "ladder") {
      contentHeights.push(node.scrollHeight + node.marginTop + node.marginBottom);
      return;
    }
    contentHeights.push(node.frameHeight);
    for (let index = 1; index < node.children.length; index += 1) gapHeights.push(node.rowGap);
    for (const child of node.children) collect(child);
  };
  collect(root);
  return railCardContentHeight(contentHeights, [], gapHeights);
}

export interface ClampedLevelLabelParts {
  /** "CLAMPED TOP" or "CLAMPED BOTTOM": the edge mark, never dropped; "" for an in-range label (box 5). */
  edge: string;
  name: string;
  /** The wall's short scope token (A2 SCOPE), never dropped. */
  scope: string;
  price: string;
  /** The distance to spot without its separator, or "" when there is no spot. */
  away: string;
}

export type ClampedLevelLabelFit = {
  variant: "full" | "without-away" | "without-name";
  visibleText: string;
  title: string;
  price: string;
  showName: boolean;
  showAway: boolean;
  /** False when even the minimum (edge, scope, price) crosses the width: stamped on the face, never hidden. */
  fits: boolean;
};

/**
 * Fit an SVG clamped-level label by measured width. DESK-n164 RULED 5 amends the order's G2 ladder
 * under A2 SCOPE: the distance yields first, then the wall name; the edge mark, the scope token and
 * the price always stay, and the full unabridged label survives in `title`.
 */
export function fitClampedLevelLabel(
  parts: ClampedLevelLabelParts,
  availableWidth: number,
  measure: (text: string, role: "meta" | "price") => number,
): ClampedLevelLabelFit {
  if (!Number.isFinite(availableWidth) || availableWidth < 0) {
    throw new RangeError(
      `FOCUS FIT REFUSED — clamped label width ${String(availableWidth)} is not non-negative and finite`,
    );
  }
  // An in-range label (box 5) has no edge mark: the same ladder, without the mark's separator.
  const edge = parts.edge === "" ? "" : `${parts.edge} · `;
  const named = `${edge}${parts.name} · ${parts.scope} `;
  const away = parts.away === "" ? "" : ` · ${parts.away}`;
  const title = `${named}${parts.price}${away}`;
  const candidates = [
    { variant: "full" as const, meta: named, showName: true, away, showAway: away !== "" },
    { variant: "without-away" as const, meta: named, showName: true, away: "", showAway: false },
    { variant: "without-name" as const, meta: `${edge}${parts.scope} `, showName: false, away: "", showAway: false },
  ];
  let width = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    width = measure(candidate.meta, "meta") + measure(parts.price, "price") + measure(candidate.away, "meta");
    if (width <= availableWidth || candidate.variant === "without-name") {
      return {
        variant: candidate.variant,
        visibleText: `${candidate.meta}${parts.price}${candidate.away}`,
        title,
        price: parts.price,
        showName: candidate.showName,
        showAway: candidate.showAway,
        fits: width <= availableWidth,
      };
    }
  }
  throw new Error("FOCUS FIT REFUSED — the clamped label ladder has no minimum variant");
}

/** A clipped ladder viewport gives up its partial final row, never part of the row itself. */
export function snapLadderViewportHeight(availableHeight: number, rowHeight: number): number {
  if (!Number.isFinite(availableHeight) || availableHeight < 0 ||
      !Number.isFinite(rowHeight) || rowHeight <= 0) {
    throw new RangeError(
      `FOCUS FIT REFUSED — ladder viewport ${String(availableHeight)} / row ${String(rowHeight)} is invalid`,
    );
  }
  return Math.floor(availableHeight / rowHeight) * rowHeight;
}

/** Keep the centred ladder origin on the same row grid, including at the scroll extent. */
export function snapLadderScrollTop(
  scrollTop: number,
  rowHeight: number,
  maxScrollTop: number,
  phase = 0,
): number {
  if (![scrollTop, rowHeight, maxScrollTop, phase].every(Number.isFinite) ||
      scrollTop < 0 || rowHeight <= 0 || maxScrollTop < 0 || phase < 0 || phase >= rowHeight) {
    throw new RangeError(
      `FOCUS FIT REFUSED — ladder scroll ${String(scrollTop)} / row ${String(rowHeight)} / max ${String(maxScrollTop)} / phase ${String(phase)} is invalid`,
    );
  }
  if (maxScrollTop < phase) return 0;
  const lastAlignedOrigin = Math.floor((maxScrollTop - phase) / rowHeight) * rowHeight + phase;
  const snapped = Math.max(0, Math.round((scrollTop - phase) / rowHeight)) * rowHeight + phase;
  return Math.min(snapped, lastAlignedOrigin);
}

/**
 * The strike GLYPH grid's offset inside the ladder's row slots: a strike label's box overhangs its
 * slot, so an edge on a slot boundary cuts a glyph. `glyphBottom` is the first row's glyph bottom
 * in scroll-content coordinates; an edge at this phase (mod the row) falls in the gap between glyphs.
 */
export function ladderGlyphPhase(glyphBottom: number, rowHeight: number): number {
  if (!Number.isFinite(glyphBottom) || !Number.isFinite(rowHeight) || rowHeight <= 0) {
    throw new RangeError(
      `FOCUS FIT REFUSED — glyph bottom ${String(glyphBottom)} / row ${String(rowHeight)} is invalid`,
    );
  }
  return ((glyphBottom % rowHeight) + rowHeight) % rowHeight;
}

function fittedCell(
  id: string,
  x: number,
  y: number,
  w: number,
  h: number,
  contentHeight: number,
): FocusCellFit {
  return {
    id, x, y, w, h,
    clientWidth: w,
    clientHeight: h,
    scrollWidth: w,
    scrollHeight: contentHeight,
  };
}

function measuredHeight(name: string, value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`FOCUS FIT REFUSED — ${name} content height ${String(value)} is not positive and finite`);
  }
  return Math.ceil(value);
}

/**
 * Executable measured geometry for FOCUS-FIT-1.
 *
 * At widths >= 1440px the four tides stay in one row and the measured dealer cards form the
 * right column. Below 1440px they fold two-by-two and the dealer column drops underneath. A
 * short folded viewport refuses instead of manufacturing postage stamps or clipping content.
 */
export function fitFocusCells(
  width: number,
  height: number,
  content: FocusContentHeights,
): FocusFit {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error(`FOCUS FIT REFUSED — invalid viewport ${width}×${height}`);
  }
  if (!content || !Array.isArray(content.tides) || content.tides.length !== 4) {
    throw new Error("FOCUS FIT REFUSED — exactly four measured tide content heights are required");
  }
  const chartContentH = measuredHeight("chart", content.chart);
  const vsplitContentH = measuredHeight("V-split pane", content.vsplit);
  const tideContentHeights = content.tides.map((value, index) =>
    measuredHeight(`tide ${index}`, value)) as [number, number, number, number];
  const rail0dteH = measuredHeight("GEX-2", content.rail0dte);
  const railAllExpiryH = measuredHeight("dealer", content.railAllExpiry);
  const inset = FOCUS_VIEWPORT_INSET_PX / 2;
  const usableW = Math.floor(width - FOCUS_VIEWPORT_INSET_PX);
  const usableH = Math.floor(height - FOCUS_VIEWPORT_INSET_PX);
  const stacked = width < FOCUS_STACK_BREAKPOINT_PX;
  const folded = width < FOCUS_FOLD_BREAKPOINT_PX;
  const chartMinimumH = Math.max(FOCUS_PROPORTIONS.chart.minPx, chartContentH, vsplitContentH);
  const tideH = Math.max(FOCUS_PROPORTIONS.tide.minPx, ...tideContentHeights);
  const fittedRailHeights = { rail0dte: rail0dteH, railAllExpiry: railAllExpiryH };
  const railCellBudgetH = focusRailCellHeightBudget(usableH);
  fittedRailHeights.rail0dte = Math.min(fittedRailHeights.rail0dte, railCellBudgetH);
  fittedRailHeights.railAllExpiry = Math.min(fittedRailHeights.railAllExpiry, railCellBudgetH);
  const railStackH = fittedRailHeights.rail0dte + FOCUS_GAP_PX + fittedRailHeights.railAllExpiry;
  if (usableW <= FOCUS_GAP_PX) {
    throw new Error(`FOCUS FIT REFUSED — viewport ${width}×${height} leaves no measured content width`);
  }
  if (stacked) {
    const chart = fittedCell("chart", inset, inset, usableW, chartMinimumH, chartContentH);
    const tideY = chart.y + chart.h + FOCUS_GAP_PX;
    const tides = FOCUS_TIDE_ROW.map((scope, index) => fittedCell(
      `tide-${scope.id}`,
      inset,
      tideY + index * (tideH + FOCUS_GAP_PX),
      usableW,
      tideH,
      tideContentHeights[index],
    )) as FocusFit["tides"];
    const tideRowsH = 4 * tideH + 3 * FOCUS_GAP_PX;
    const tideRow = fittedCell("tide-row", inset, tideY, usableW, tideRowsH, tideRowsH);
    const railY = tideY + tideRowsH + FOCUS_GAP_PX;
    const rail0dte = fittedCell("rail-0dte", inset, railY, usableW, rail0dteH, rail0dteH);
    const railAllExpiry = fittedCell(
      "rail-all-expiry",
      inset,
      railY + rail0dteH + FOCUS_GAP_PX,
      usableW,
      railAllExpiryH,
      railAllExpiryH,
    );
    return {
      viewport: { width, height },
      usable: { x: inset, y: inset, w: usableW, h: usableH },
      stacked, folded, chart, tideRow, tides, rail0dte, railAllExpiry,
      renderCells: [chart, ...tides, rail0dte, railAllExpiry],
    };
  }
  const railW = clamp(
    Math.floor(usableW * FOCUS_PROPORTIONS.rail.fraction),
    FOCUS_PROPORTIONS.rail.minPx,
    FOCUS_PROPORTIONS.rail.maxPx,
  );
  const leftW = folded ? usableW : usableW - FOCUS_GAP_PX - railW;
  if (leftW <= 0) {
    throw new Error(`FOCUS FIT REFUSED — viewport ${width}×${height} leaves no left column`);
  }
  const tideColumns = folded ? 2 : 4;
  const tideW = (leftW - (tideColumns - 1) * FOCUS_GAP_PX) / tideColumns;
  if (tideW <= 0) {
    throw new Error(`FOCUS FIT REFUSED — viewport ${width}×${height} leaves no tide-cell width`);
  }

  const tideRowsH = folded ? 2 * tideH + FOCUS_GAP_PX : tideH;
  const droppedRailH = folded ? railStackH : 0;
  // The aggregate tide/rail heights already include their internal row gaps; only gaps between
  // those aggregates remain here (chart→tides and, when folded, tides→dropped rail).
  const verticalGaps = folded ? 2 * FOCUS_GAP_PX : FOCUS_GAP_PX;
  const chartH = usableH - tideRowsH - droppedRailH - verticalGaps;
  if (chartH < chartMinimumH) {
    const suffix = folded ? " in folded composition" : "";
    throw new Error(
      `FOCUS FIT REFUSED — viewport ${width}×${height} cannot hold measured chart ${chartMinimumH}px ` +
      `+ tide floor ${tideH}px${suffix}`,
    );
  }

  const chart = fittedCell("chart", inset, inset, leftW, chartH, chartContentH);
  const tideY = inset + chartH + FOCUS_GAP_PX;
  const tideRow = fittedCell("tide-row", inset, tideY, leftW, tideRowsH, tideRowsH);
  const tides = FOCUS_TIDE_ROW.map((scope, index) => {
    const row = folded ? Math.floor(index / 2) : 0;
    const column = folded ? index % 2 : index;
    return fittedCell(
      `tide-${scope.id}`,
      inset + column * (tideW + FOCUS_GAP_PX),
      tideY + row * (tideH + FOCUS_GAP_PX),
      tideW,
      tideH,
      tideContentHeights[index],
    );
  }) as FocusFit["tides"];
  const railX = folded ? inset : inset + leftW + FOCUS_GAP_PX;
  const railY = folded ? tideY + tideRowsH + FOCUS_GAP_PX : inset;
  const fittedRailW = folded ? usableW : railW;
  const rail0dte = fittedCell(
    "rail-0dte", railX, railY, fittedRailW, fittedRailHeights.rail0dte, fittedRailHeights.rail0dte,
  );
  const railAllExpiry = fittedCell(
    "rail-all-expiry",
    railX,
    railY + fittedRailHeights.rail0dte + FOCUS_GAP_PX,
    fittedRailW,
    fittedRailHeights.railAllExpiry,
    fittedRailHeights.railAllExpiry,
  );
  return {
    viewport: { width, height },
    usable: { x: inset, y: inset, w: usableW, h: usableH },
    folded, chart, tideRow, tides, rail0dte, railAllExpiry,
    renderCells: [chart, ...tides, rail0dte, railAllExpiry],
  };
}

export function fitMatchesRender(
  fit: FocusFit,
  rendered: FocusRenderFixture,
  tolerancePx = 2,
): boolean {
  if (rendered.viewport.width !== fit.viewport.width || rendered.viewport.height !== fit.viewport.height) {
    return false;
  }
  if (rendered.cells.length !== fit.renderCells.length) return false;
  const expected = new Map(fit.renderCells.map((cell) => [cell.id, cell]));
  const seen = new Set<string>();
  for (const cell of rendered.cells) {
    const wanted = expected.get(cell.id);
    if (!wanted || seen.has(cell.id)) return false;
    seen.add(cell.id);
    if (![cell.x, cell.y, cell.w, cell.h].every(Number.isFinite)) return false;
    if (Math.abs(cell.x - wanted.x) > tolerancePx || Math.abs(cell.y - wanted.y) > tolerancePx ||
        Math.abs(cell.w - wanted.w) > tolerancePx || Math.abs(cell.h - wanted.h) > tolerancePx) return false;
    if (cell.y + cell.h > rendered.viewport.height + tolerancePx) return false;
  }
  return seen.size === expected.size;
}

export function defaultFocusLogicalRange(barCount: number): FocusLogicalRange {
  if (!Number.isInteger(barCount) || barCount <= 0) {
    throw new Error(`FOCUS ZOOM REFUSED — invalid bar count ${barCount}`);
  }
  return {
    from: Math.max(0, barCount - FOCUS_DEFAULT_BARS),
    to: barCount - 1 + FOCUS_RIGHT_MARGIN_BARS,
  };
}

/** HUD-390-CLOSED-HEADER-1: the chart body a focus cell keeps under any measured chrome (the tide floor's size). */
export const FOCUS_CHART_BODY_FLOOR_PX = 240;

/** The focus chart cell's floor: the 520 chart floor, or the measured chrome plus the body floor when taller. */
export function focusChartMinimumHeight(chromePx: number): number {
  return Math.max(FOCUS_PROPORTIONS.chart.minPx, Math.ceil(chromePx) + FOCUS_CHART_BODY_FLOOR_PX);
}

/** Shared by MaximizeFrame and focus cells: subtract panel chrome from the measured frame. */
export function fittedTargetHeight(
  frameClientHeight: number,
  panelScrollHeight: number,
  targetClientHeight: number,
  borderHeight = 0,
): number {
  const chromeHeight = Math.max(0, panelScrollHeight - targetClientHeight + borderHeight);
  return Math.max(1, Math.floor(frameClientHeight - chromeHeight));
}

export interface FocusPreferences {
  primaryA: string;
  primaryB: string;
  rangeTicks: 8 | 12;
  ranges?: Partial<Record<RungKey, FocusLogicalRange>>;
}

interface PreferenceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const STORAGE_KEY = "spx-hub.focus-layout.v1";
const DEFAULT_PREFERENCES: FocusPreferences = { primaryA: "1", primaryB: "12", rangeTicks: 8 };
const RUNG_KEYS = new Set(RUNG_LADDER.flatMap((rung) =>
  rung.kind === "tf" ? [rungKey(rung), rung.tf] : [rungKey(rung)],
));

function browserStorage(): PreferenceStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch (error) {
    console.warn(`[FocusLayout] preference storage unavailable — ${String(error)}`);
    return null;
  }
}

function admittedRange(value: unknown): FocusLogicalRange | null {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as Partial<FocusLogicalRange>;
  return typeof candidate.from === "number" && Number.isFinite(candidate.from) &&
    typeof candidate.to === "number" && Number.isFinite(candidate.to) && candidate.from < candidate.to
    ? { from: candidate.from, to: candidate.to }
    : null;
}

export function readFocusPreferences(storage: PreferenceStorage | null = browserStorage()): FocusPreferences {
  if (storage === null) return { ...DEFAULT_PREFERENCES };
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_PREFERENCES };
    const value = JSON.parse(raw) as Partial<FocusPreferences>;
    const ranges = Object.fromEntries(Object.entries(value.ranges ?? {}).flatMap(([key, range]) => {
      const admitted = admittedRange(range);
      return RUNG_KEYS.has(key) && admitted !== null ? [[key, admitted]] : [];
    })) as Partial<Record<RungKey, FocusLogicalRange>>;
    return {
      primaryA: RUNG_KEYS.has(value.primaryA ?? "") ? value.primaryA! : DEFAULT_PREFERENCES.primaryA,
      primaryB: RUNG_KEYS.has(value.primaryB ?? "") ? value.primaryB! : DEFAULT_PREFERENCES.primaryB,
      rangeTicks: value.rangeTicks === 12 ? 12 : 8,
      ranges,
    };
  } catch (error) {
    console.warn(`[FocusLayout] preference read failed — ${String(error)}`);
    return { ...DEFAULT_PREFERENCES };
  }
}

export function writeFocusPreferences(
  storage: PreferenceStorage | null = browserStorage(),
  value: FocusPreferences,
): void {
  if (storage === null) return;
  if (!RUNG_KEYS.has(value.primaryA) || !RUNG_KEYS.has(value.primaryB)) {
    throw new Error("FOCUS PREFERENCE REFUSED — unknown chart rung");
  }
  if (value.rangeTicks !== 8 && value.rangeTicks !== 12) {
    throw new Error("FOCUS PREFERENCE REFUSED — range rung must be 8t or 12t");
  }
  try {
    const ranges = value.ranges ?? readFocusPreferences(storage).ranges ?? {};
    storage.setItem(STORAGE_KEY, JSON.stringify({ ...value, ranges }));
  } catch (error) {
    console.warn(`[FocusLayout] preference write failed — ${String(error)}`);
  }
}

export function readFocusRange(
  rung: RungKey,
  storage: PreferenceStorage | null = browserStorage(),
): FocusLogicalRange | null {
  if (!RUNG_KEYS.has(rung)) throw new Error(`FOCUS ZOOM REFUSED — unknown chart rung ${rung}`);
  return readFocusPreferences(storage).ranges?.[rung] ?? null;
}

export function writeFocusRange(
  rung: RungKey,
  range: FocusLogicalRange | null,
  storage: PreferenceStorage | null = browserStorage(),
): void {
  if (!RUNG_KEYS.has(rung)) throw new Error(`FOCUS ZOOM REFUSED — unknown chart rung ${rung}`);
  if (range !== null && admittedRange(range) === null) {
    throw new Error("FOCUS ZOOM REFUSED — logical range must be finite and ordered");
  }
  const current = readFocusPreferences(storage);
  const ranges = { ...(current.ranges ?? {}) };
  if (range === null) delete ranges[rung];
  else ranges[rung] = range;
  writeFocusPreferences(storage, { ...current, ranges });
}
